import { readItem, storageKeys, writeItem } from './storage';

const settingsKey = storageKeys.settings;

function getSettings() {
  try {
    let settings = JSON.parse(readItem(settingsKey));
    if (!settings) return {};
    return settings;
  } catch (error) {
    // A corrupt blob would otherwise throw on every read, taking the page with it
    return {};
  }
}

export function getSetting(settingKey) {
  let settings = getSettings();
  return settings[settingKey];
}

export function setSetting(name, value) {
  let settings = getSettings();

  settings[name] = value;
  // Makes room and retries if the store is full, rather than throwing into whatever was saving
  writeItem(settingsKey, JSON.stringify(settings));

  return settings;
}

// Told whenever a setting hook changes a value, so every other hook on that key follows it: the
// players open at once (a modal's, the intensity panel's) share their levels
const SETTING_EVENT = 'mg-setting-change';

export const announceSetting = (name, value) =>
  window.dispatchEvent(new CustomEvent(SETTING_EVENT, { detail: { name, value } }));

/** Calls `onChange(value)` when a setting hook elsewhere changes `name`. Returns the unsubscribe. */
export const listenToSetting = (name, onChange) => {
  const handle = (event) => event.detail.name === name && onChange(event.detail.value);

  window.addEventListener(SETTING_EVENT, handle);

  return () => window.removeEventListener(SETTING_EVENT, handle);
};

export const settingKeys = {
  darkTheme: 'dark-theme',
  // Unset until picked in Settings, so the browser's language decides until then
  language: 'language',
  // Set while a language picked here hasn't reached the account yet, so it isn't overwritten by it
  languageUnsaved: 'language-unsaved',
  stopScrollingKey: 'stopScrolling',
  hideChordsPlugin: 'hide-chords-plugin/hide-chords',
  overviewPlugin: 'overview-plugin/enabled',
  songPlayCooldowns: 'song-play-cooldowns',
  preferSharps: 'prefer-sharps',
  // Unticked rather than ticked, so a service name used for the first time starts ticked
  songFilterExcludedServices: 'song-filter-excluded-services',
  showPlugins: 'show-plugins',
  welcomeModalData: 'welcome-modal-data',
  // The app version this device last showed (or skipped) release notes for
  lastSeenVersion: 'last-seen-version',
  metronomeVolume: 'metronome-volume',
  metronomeSound: 'metronome-sound',
  metronomeContinuous: 'metronome-continuous',
  metronomeFollowAutoScroll: 'metronome-follow-auto-scroll',
  metronomeAccentBeat: 'metronome-accent-beat',
  metronomeSubdivision: 'metronome-subdivision',
  chordBarsClickVolume: 'chord-bars-click-volume',
  chordBarsInstrumentVolume: 'chord-bars-instrument-volume',
  // The rhythm picked in the player for one song, over what the song is set to: { [songId]: choice }
  chordBarsSongRhythms: 'chord-bars-song-rhythms',
  chordBarsDrums: 'chord-bars-drums',
  chordBarsDrumVolume: 'chord-bars-drum-volume',
  chordBarsBass: 'chord-bars-bass',
  chordBarsBassVolume: 'chord-bars-bass-volume',
  chordBarsPad: 'chord-bars-pad',
  chordBarsPadVolume: 'chord-bars-pad-volume',
  chordBarsBrass: 'chord-bars-brass',
  chordBarsBrassVolume: 'chord-bars-brass-volume',
  chordBarsGuitar: 'chord-bars-guitar',
  chordBarsGuitarVolume: 'chord-bars-guitar-volume',
  chordBarsAcoustic: 'chord-bars-acoustic',
  chordBarsAcousticVolume: 'chord-bars-acoustic-volume',
  chordBarsViolin: 'chord-bars-violin',
  chordBarsViolinVolume: 'chord-bars-violin-volume',
  chordBarsVoicing: 'chord-bars-voicing',
  chordBarsMelody: 'chord-bars-melody',
  // The intensity panel's own, off unless turned on there: it is for hearing the arrangement
  intensityMelody: 'intensity-melody',
  chordBarsLyrics: 'chord-bars-lyrics',
  chordBarsRepeats: 'chord-bars-repeats',
  chordBarsPlayThrough: 'chord-bars-play-through',
  chordBarsCallouts: 'chord-bars-callouts',
  // Off plays a run as if no part had an intensity set
  chordBarsIntensity: 'chord-bars-intensity',
  chordBarsCalloutVolume: 'chord-bars-callout-volume',
  // The builder's own, kept apart from the player's: there the words replace the chart, so
  // sharing one key would hand View a part with no grid on it because a chart was being written
  chordBarsBuilderLyrics: 'chord-bars-builder-lyrics',
  // The last pick in InstrumentRow's sheet, shared by the chart builder and the MIDI upload
  lastInstrument: 'last-instrument',
  melodyVolume: 'melody-volume',
  scrollSpeedPreferences: 'scroll-speed-preferences',
  autoScrollLyricsTap: 'auto-scroll-lyrics-tap',
  rememberScrollSpeed: 'remember-scroll-speed',
  smartScroll: 'smart-scroll',
  // Outlines the part Smart Scroll is in. Off by default while it's tried out
  highlightPlayingPart: 'highlight-playing-part',
  timingRecorder: 'timing-recorder',
  smartScrollDebug: 'smart-scroll-debug',
  showDuplicateIcon: 'show-duplicate-icon',
  showMidiIcon: 'show-midi-icon',
  fontSize: 'font-size',
  superscriptQualifiers: 'superscript-qualifiers',
  chordSymbols: 'chord-symbols',
  colorBorrowedChords: 'color-borrowed-chords',
  showChordOnTap: 'show-chord-on-tap',
  superScroll: 'scroll-speed-overlay',
  superMetronome: 'metronome-overlay',
  repeatCountAsChip: 'repeat-count-as-chip',
  colorTheme: 'color-theme',
  lastPublicSongAddedDate: 'last-public-song-added-date',
  xmlExportOptions: 'xml-export-options',
};
